/**
 * PlaybooksListPage
 *
 * Shows all playbooks for the workspace, grouped by status (Active / Archived).
 * Mirrors PropensityListPage structure: header + create CTA + card grid + empty state.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import ArticleRounded from '@mui/icons-material/ArticleRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import GroupRounded from '@mui/icons-material/GroupRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import UnarchiveRounded from '@mui/icons-material/UnarchiveRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import { useNavigate } from 'react-router-dom';
import { listPlaybooks, triggerPlaybookRun, updatePlaybook, deletePlaybook } from '../../services/playbooksService';
import { useCopilotStore } from '../copilot/stores/useCopilotStore';
import type { PlaybookSummary, PlaybookRunStatus } from '../../types/playbooks';
import { SnackbarContext } from '../../ui/SnackbarProvider';
import { ConfirmDialog } from '../../components/dialog';
import { formatRelativeTime } from '../../utils/date';
import SettingsPageHeader from '../settings/components/SettingsPageHeader';
import { EntityTile, type StatusTone } from '../../ui-kit';
import { EditDetailsModal, tileMenu } from '../agents/tileMenu';
import SchedulesManager from './components/SchedulesManager';
import CloseRounded from '@mui/icons-material/CloseRounded';

type SortKey = 'recent' | 'created' | 'name' | 'runs';

/** Interprets period_strategy into a short card chip label. */
function describePeriodLabel(strategy?: string | null): { label: string; title: string } {
  const s = (strategy || '').trim();
  if (!s) return { label: 'At run time', title: 'Reporting period: decided on each run' };
  const fixed = s.match(/^fixed window:\s*(.+)$/i);
  if (fixed) return { label: `Fixed · ${fixed[1]}`, title: `Reporting period: fixed at ${fixed[1]}` };
  return { label: `Rolling · ${s}`, title: `Reporting period: rolling — the ${s}` };
}

/** Output → label + icon. A Word report is always produced; an attached Excel
 *  template is an additional filled output. */
function describeOutput(_format: string, hasTemplate?: boolean): { label: string; title: string; Icon: typeof ArticleRounded } {
  if (hasTemplate) {
    return {
      label: 'Word + Excel',
      title: 'Writes a Word report and fills your Excel template each run',
      Icon: TableChartRounded,
    };
  }
  return { label: 'Word', title: 'Generates a Word document each run', Icon: ArticleRounded };
}

// ─── Status chip helper ────────────────────────────────────────────────────

function RunStatusChip({ status }: { status: PlaybookRunStatus }) {
  const map: Record<PlaybookRunStatus, { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' }> = {
    completed: { label: 'Completed', color: 'success' },
    failed: { label: 'Failed', color: 'error' },
    running: { label: 'Running', color: 'info' },
    waiting_on_user: { label: 'Awaiting input', color: 'warning' },
    pending: { label: 'Pending', color: 'warning' },
  };
  const { label, color } = map[status] ?? { label: status, color: 'default' };
  return (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 600 }}
    />
  );
}

// ─── Playbook card ────────────────────────────────────────────────────────

interface CardProps {
  playbook: PlaybookSummary;
  onRun: (id: string) => void;
  onArchiveToggle: (pb: PlaybookSummary) => void;
  onDelete: (pb: PlaybookSummary) => void;
  onSchedule: (pb: PlaybookSummary) => void;
  running: boolean;
}

function PlaybookCard({ playbook, onRun, onArchiveToggle, onDelete, onSchedule, running }: CardProps) {
  const navigate = useNavigate();
  const isArchived = playbook.status === 'archived';
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const period = describePeriodLabel(playbook.period_strategy);
  const output = describeOutput(playbook.output_format, playbook.has_output_template);
  const OutputIcon = output.Icon;

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea
        onClick={() => navigate(`/agents/${playbook.playbook_id}`)}
        sx={{ flex: 1, alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }}
      >
        <CardContent sx={{ p: 2, pb: 1.75, flex: 1, width: '100%' }}>
          {/* Header: output tile + name/meta + visibility + kebab */}
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Tooltip title={output.title}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <OutputIcon sx={{ fontSize: 19 }} />
              </Box>
            </Tooltip>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Tooltip title={playbook.name}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  noWrap
                  sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}
                >
                  {playbook.name}
                </Typography>
              </Tooltip>
              <Tooltip title={period.title}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {output.label} · {period.label}
                </Typography>
              </Tooltip>
            </Box>

            <Stack direction="row" spacing={0.25} flexShrink={0} alignItems="center" sx={{ mt: -0.25 }}>
              <Tooltip title={playbook.visibility === 'private' ? 'Personal — only you can see this' : 'Team — shared with your workspace'}>
                {playbook.visibility === 'private'
                  ? <LockRounded sx={{ fontSize: 15, color: 'text.disabled' }} />
                  : <GroupRounded sx={{ fontSize: 15, color: 'text.disabled' }} />}
              </Tooltip>
              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  sx={{ p: 0.25 }}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMenuAnchor(e.currentTarget); }}
                >
                  <MoreVertRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Status line: last run outcome + run count + when */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.75 }}>
            {playbook.last_run_status ? (
              <RunStatusChip status={playbook.last_run_status} />
            ) : (
              <Chip
                label="Never run"
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 600, color: 'text.disabled' }}
              />
            )}
            <Typography variant="caption" color="text.disabled" noWrap>
              {playbook.run_count} run{playbook.run_count !== 1 ? 's' : ''}
              {playbook.last_run_at ? ` · ${formatRelativeTime(playbook.last_run_at)}` : ''}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        {!isArchived && (
          <MenuItem
            onClick={() => { closeMenu(); onRun(playbook.playbook_id); }}
            disabled={running}
          >
            <PlayArrowRounded fontSize="small" sx={{ mr: 1.5 }} />
            Run now
          </MenuItem>
        )}
        <MenuItem onClick={() => { closeMenu(); navigate(`/agents/${playbook.playbook_id}`); }}>
          <OpenInNewRounded fontSize="small" sx={{ mr: 1.5 }} />
          Open
        </MenuItem>
        <MenuItem onClick={() => { closeMenu(); onSchedule(playbook); }}>
          <ScheduleRounded fontSize="small" sx={{ mr: 1.5 }} />
          Schedule
        </MenuItem>
        <MenuItem onClick={() => { closeMenu(); onArchiveToggle(playbook); }}>
          {isArchived
            ? <UnarchiveRounded fontSize="small" sx={{ mr: 1.5 }} />
            : <Inventory2Rounded fontSize="small" sx={{ mr: 1.5 }} />}
          {isArchived ? 'Restore' : 'Archive'}
        </MenuItem>
        <Divider />
        <MenuItem sx={{ color: 'error.main' }} onClick={() => { closeMenu(); onDelete(playbook); }}>
          <DeleteRounded fontSize="small" sx={{ mr: 1.5 }} />
          Delete
        </MenuItem>
      </Menu>

      <Divider />

      {/* Actions: Run now + Schedule */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}
      >
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={running ? <CircularProgress size={12} color="inherit" /> : <PlayArrowRounded sx={{ fontSize: 15 }} />}
          disabled={running || isArchived}
          onClick={(e) => { e.stopPropagation(); onRun(playbook.playbook_id); }}
          sx={{ fontSize: '0.8125rem', textTransform: 'none', fontWeight: 600, flex: 1 }}
        >
          {running ? 'Starting…' : isArchived ? 'Archived' : 'Run now'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ScheduleRounded sx={{ fontSize: 15 }} />}
          disabled={isArchived}
          onClick={(e) => { e.stopPropagation(); onSchedule(playbook); }}
          sx={{
            fontSize: '0.8125rem',
            textTransform: 'none',
            fontWeight: 600,
            flex: 1,
            bgcolor: 'background.paper',
          }}
        >
          {playbook.schedule_count ? `Schedule (${playbook.schedule_count})` : 'Schedule'}
        </Button>
      </Stack>
    </Card>
  );
}

// ─── Playbook as a tile, beside the workspace's agents ────────────────────────

const RUN_STATUS: Record<PlaybookRunStatus, { tone: StatusTone; label: string }> = {
  completed: { tone: 'good', label: 'Last run completed' },
  failed: { tone: 'bad', label: 'Last run failed' },
  running: { tone: 'warning', label: 'Running' },
  waiting_on_user: { tone: 'warning', label: 'Awaiting input' },
  pending: { tone: 'warning', label: 'Pending' },
};

/**
 * A saved analysis drawn the way the workspace's other agents are, so the
 * Agents page is one grid of one kind of thing. Run now and Schedule stay as
 * buttons; Open, Archive and Delete sit behind ⋯.
 */
function PlaybookTile({
  playbook, onRun, onArchiveToggle, onDelete, onSchedule, onEdit, running,
}: CardProps & { onEdit: (pb: PlaybookSummary) => void }) {
  const navigate = useNavigate();
  const isArchived = playbook.status === 'archived';
  const output = describeOutput(playbook.output_format, playbook.has_output_template);
  const OutputIcon = output.Icon;
  const status = isArchived
    ? { tone: 'neutral' as StatusTone, label: 'Archived' }
    : playbook.last_run_status ? RUN_STATUS[playbook.last_run_status] : { tone: 'neutral' as StatusTone, label: 'Never run' };

  return (
    <EntityTile
      title={playbook.name}
      // What comes out of a run, and nothing else: the goal is a paragraph, and
      // a card in a row of agents is not where anybody reads one.
      meta={output.label}
      icon={<OutputIcon sx={{ fontSize: 20 }} />}
      status={status}
      onOpen={() => navigate(`/agents/${playbook.playbook_id}`)}
      buttons={[
        {
          key: 'run',
          label: running ? 'Starting…' : 'Run now',
          icon: <PlayArrowRounded sx={{ fontSize: 16 }} />,
          variant: 'brand',
          disabled: running || isArchived,
          loading: running,
          onClick: () => onRun(playbook.playbook_id),
        },
        {
          key: 'schedule',
          label: playbook.schedule_count ? `Schedule (${playbook.schedule_count})` : 'Schedule',
          icon: <ScheduleRounded sx={{ fontSize: 16 }} />,
          disabled: isArchived,
          onClick: () => onSchedule(playbook),
        },
      ]}
      actions={tileMenu({
        onOpen: () => navigate(`/agents/${playbook.playbook_id}`),
        onEdit: () => onEdit(playbook),
        archived: isArchived,
        onArchiveToggle: () => onArchiveToggle(playbook),
        onDelete: () => onDelete(playbook),
      })}
    />
  );
}

/** The schedules window, shared by the card list and the tile layout. */
function SchedulesDialog({
  target, onClose, onCountChange,
}: {
  target: PlaybookSummary | null;
  onClose: () => void;
  onCountChange: (count: number) => void;
}) {
  return (
    <Dialog
      open={Boolean(target)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pr: 6 }}>
        Schedules
        {target && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {target.name}
          </Typography>
        )}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="small"
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {target && (
          <SchedulesManager
            playbookId={target.playbook_id}
            requirePeriodWindow={Boolean(target.has_manual_run)}
            onCountChange={onCountChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function PlaybooksListPage({
  renderAsTiles,
}: {
  /**
   * For /agents in a workspace that has agents: each saved analysis becomes a
   * tile like theirs, handed to the caller to lay out in the one grid, and
   * this page draws nothing else of its own — no header, no New analysis, no
   * search. Run now, Schedule, Archive and Delete all work as before.
   */
  renderAsTiles?: (tiles: React.ReactNode[], loading: boolean) => React.ReactNode;
} = {}) {
  const navigate = useNavigate();
  const { notify } = useContext(SnackbarContext);

  const [playbooks, setPlaybooks] = useState<PlaybookSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('recent');
  const [deleteTarget, setDeleteTarget] = useState<PlaybookSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState<PlaybookSummary | null>(null);
  const [editTarget, setEditTarget] = useState<PlaybookSummary | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPlaybooks();
      setPlaybooks(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleRun = async (playbookId: string) => {
    setRunningIds((prev) => new Set(prev).add(playbookId));
    try {
      const result = await triggerPlaybookRun(playbookId);
      // Use pendingAutoSend so CopilotPage auto-fires the stream on landing
      const store = useCopilotStore.getState();
      store.resetSession();
      store.setThreadId(result.thread_id);
      store.setInputText(result.goal_message);
      store.setPendingAgentType('playbook');
      store.setPendingAutoSend(true);
      navigate(`/copilot/${result.thread_id}`);
    } catch (err: any) {
      notify(err.message || 'Failed to start run', 'error');
      setRunningIds((prev) => { const s = new Set(prev); s.delete(playbookId); return s; });
    }
  };

  const handleArchiveToggle = async (pb: PlaybookSummary) => {
    const nextStatus = pb.status === 'active' ? 'archived' : 'active';
    try {
      await updatePlaybook(pb.playbook_id, { status: nextStatus });
      setPlaybooks((prev) => prev.map((p) => (p.playbook_id === pb.playbook_id ? { ...p, status: nextStatus } : p)));
      notify(nextStatus === 'archived' ? 'Agent archived' : 'Agent restored', 'success');
    } catch (err: any) {
      notify(err.message || 'Failed to update agent', 'error');
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePlaybook(deleteTarget.playbook_id);
      setPlaybooks((prev) => prev.filter((p) => p.playbook_id !== deleteTarget.playbook_id));
      notify('Agent deleted', 'success');
      setDeleteTarget(null);
    } catch (err: any) {
      notify(err.message || 'Failed to delete agent', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const { active, archived } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (p: PlaybookSummary) =>
      !q || p.name.toLowerCase().includes(q) || (p.goal || '').toLowerCase().includes(q);
    const sorter = (a: PlaybookSummary, b: PlaybookSummary) => {
      switch (sortKey) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created':
          return (b.created_at || '').localeCompare(a.created_at || '');
        case 'runs':
          return (b.run_count || 0) - (a.run_count || 0);
        case 'recent':
        default:
          return (b.last_run_at || b.updated_at || '').localeCompare(a.last_run_at || a.updated_at || '');
      }
    };
    const filtered = playbooks.filter(matches);
    return {
      active: filtered.filter((p) => p.status === 'active').sort(sorter),
      archived: filtered.filter((p) => p.status === 'archived').sort(sorter),
    };
  }, [playbooks, query, sortKey]);

  const noMatches = !loading && !error && playbooks.length > 0 && active.length === 0 && archived.length === 0;

  const dialogs = (
    <>
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete agent?"
        description={deleteTarget
          ? `"${deleteTarget.name}" and all its run history will be permanently deleted. This cannot be undone.`
          : ''}
        tone="destructive"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirmed}
        busy={deleting}
      />
      {editTarget ? (
        <EditDetailsModal
          title={editTarget.name}
          name={editTarget.name}
          description={editTarget.goal ?? ''}
          onClose={() => setEditTarget(null)}
          onSave={async ({ name, description }) => {
            await updatePlaybook(editTarget.playbook_id, { name, goal: description });
            setPlaybooks((prev) => prev.map((p) => (
              p.playbook_id === editTarget.playbook_id ? { ...p, name, goal: description } : p)));
            notify('Agent updated', 'success');
            setEditTarget(null);
          }}
        />
      ) : null}
      <SchedulesDialog
        target={scheduleTarget}
        onClose={() => { setScheduleTarget(null); void refresh(); }}
        onCountChange={(count) => scheduleTarget && setPlaybooks((prev) => prev.map((p) => (
          p.playbook_id === scheduleTarget.playbook_id ? { ...p, schedule_count: count } : p)))}
      />
    </>
  );

  if (renderAsTiles) {
    const byRecent = (a: PlaybookSummary, b: PlaybookSummary) =>
      (b.last_run_at || b.updated_at || '').localeCompare(a.last_run_at || a.updated_at || '');
    const ordered = [
      ...playbooks.filter((p) => p.status === 'active').sort(byRecent),
      ...playbooks.filter((p) => p.status === 'archived').sort(byRecent),
    ];
    const tiles = ordered.map((pb) => (
      <PlaybookTile
        key={pb.playbook_id}
        playbook={pb}
        running={runningIds.has(pb.playbook_id)}
        onRun={handleRun}
        onArchiveToggle={handleArchiveToggle}
        onDelete={setDeleteTarget}
        onSchedule={setScheduleTarget}
        onEdit={setEditTarget}
      />
    ));
    return (
      <>
        {renderAsTiles(tiles, loading)}
        {dialogs}
      </>
    );
  }

  return (
    <Box>
      {/* Slim header — page title is rendered by DashboardLayout */}
      <SettingsPageHeader
        description="Save analyses as goal-driven agents that re-run against your latest data. Each run produces a document capturing the findings."
        action={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRounded sx={{ fontSize: 18 }} />}
            onClick={() => navigate('/copilot')}
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            New analysis
          </Button>
        }
      />

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Alert severity="error" action={<Button size="small" onClick={refresh}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {/* Empty state */}
      {!loading && !error && playbooks.length === 0 && (
        <Card variant="outlined" sx={{ textAlign: 'center', py: 6, px: 3 }}>
          <SmartToyOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            No agents yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 420, mx: 'auto' }}>
            Open a Copilot chat, complete an analysis, then use the <strong>⋮ menu → Save as Agent</strong> to
            turn it into a repeatable agent.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => navigate('/copilot')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Start a new analysis
          </Button>
        </Card>
      )}

      {/* Search + sort toolbar */}
      {!loading && !error && playbooks.length > 0 && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search agents…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            label="Sort by"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="recent">Recently run</MenuItem>
            <MenuItem value="created">Newest</MenuItem>
            <MenuItem value="name">Name (A–Z)</MenuItem>
            <MenuItem value="runs">Most runs</MenuItem>
          </TextField>
        </Stack>
      )}

      {/* No search matches */}
      {noMatches && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No agents match “{query}”.
        </Typography>
      )}

      {/* Active playbooks */}
      {!loading && !error && active.length > 0 && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Active
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {active.map((pb) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={pb.playbook_id}>
                <PlaybookCard
                  playbook={pb}
                  onRun={handleRun}
                  onArchiveToggle={handleArchiveToggle}
                  onDelete={setDeleteTarget}
                  onSchedule={setScheduleTarget}
                  running={runningIds.has(pb.playbook_id)}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Archived playbooks */}
      {!loading && !error && archived.length > 0 && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Archived
          </Typography>
          <Grid container spacing={2}>
            {archived.map((pb) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={pb.playbook_id}>
                <PlaybookCard
                  playbook={pb}
                  onRun={handleRun}
                  onArchiveToggle={handleArchiveToggle}
                  onDelete={setDeleteTarget}
                  onSchedule={setScheduleTarget}
                  running={runningIds.has(pb.playbook_id)}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {dialogs}
    </Box>
  );
}
